import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import { BulkApproveDialog, BulkReimburseDialog } from '../components/pettyCash/BulkClaimsDialogs';
import { ClaimStatusChip, WAITING_STATUSES, WaitingChip } from '../components/pettyCash/ClaimChips';
import HolderBanners from '../components/pettyCash/HolderBanners';
import pettyCashApi, { ClaimScope } from '../services/pettyCashApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { ClaimRow, PettyCashConfig } from '../types/pettyCash';
import { formatAmount, formatDate, processSettings, round2 } from '../utils/pettyCash';
import { reimburseAmount } from '../utils/pettyCashBulk';
import { saveBlob } from '../utils/formattedSoa';

/**
 * Petty Cash › Claims: the claims waiting for the user, their own, (with View All) every claim of the company, and for
 * finance the posted claims to reimburse and the claims whose original receipts have not arrived. Above them, a
 * handover or quarterly confirmation the user must do. Finance ticks claims to approve together (Waiting for me: the
 * last finance stage posts them) or to reimburse together (To reimburse).
 */

const EMPTY: Record<ClaimScope, string> = {
  waiting: 'Nothing is waiting for you',
  mine: 'No claims yet',
  all: 'No claims yet',
  reimburse: 'No posted claim is waiting for its reimbursement',
  originals: 'Every submitted claim has its original receipts',
};

const PettyCashClaims: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [companyId, setCompanyId] = useState<number>(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0);
  const [config, setConfig] = useState<PettyCashConfig | null>(null);
  const [scope, setScope] = useState<ClaimScope | null>(null);
  const [rows, setRows] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The list's own error, so a failed tab does not stay on screen after another tab loads.
  const [listError, setListError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  // Bulk approval / reimbursement: the ticked claims of the list shown.
  const [selected, setSelected] = useState<Set<string | number>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  // The company picker in the top bar switches the list.
  useEffect(() => {
    const onChange = () => setCompanyId(Number(localStorage.getItem('selectedCompanyId') || '') || 0);
    window.addEventListener('company-changed', onChange);
    return () => window.removeEventListener('company-changed', onChange);
  }, []);

  useEffect(() => {
    if (!companyId) { setError('Select a company first.'); return undefined; }
    let cancelled = false;
    setError(null);
    setConfig(null);
    pettyCashApi.config(companyId)
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        setScope(c.capabilities.isHolder ? 'mine' : 'waiting');
      })
      .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || 'Petty cash could not be opened for this company'); });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !scope) return undefined;
    let cancelled = false;
    setLoading(true);
    setListError(null);
    setSelected(new Set());
    pettyCashApi.listClaims(companyId, scope)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e: any) => { if (!cancelled) { setRows([]); setListError(e?.response?.data?.message || 'The claims could not be loaded'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, scope, reloadKey]);

  /** After a handover or confirmation: the configuration (holder, banners) and the list again, on the same tab. */
  const refresh = useCallback(async () => {
    try {
      setConfig(await pettyCashApi.config(companyId));
    } catch {
      // Keep what is shown; a reload brings it.
    }
    setReloadKey((k) => k + 1);
  }, [companyId]);

  const exportList = async () => {
    if (!scope) return;
    setExporting(true);
    try {
      const { blob, fileName } = await pettyCashApi.exportClaims(companyId, scope);
      saveBlob(blob, fileName);
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || e?.message || 'The claims could not be exported', severity: 'error' }));
    } finally {
      setExporting(false);
    }
  };

  const currency = config?.company.currency || '';
  const columns: ResponsiveTableColumn<ClaimRow>[] = useMemo(() => [
    { id: 'no', header: 'Claim', cell: (r) => <Typography variant="body2" fontWeight={600}>{r.claimNo || 'Draft'}</Typography>, width: 150 },
    { id: 'holder', header: 'Holder', cell: (r) => r.holderName },
    {
      id: 'alloc',
      header: 'Project / department',
      cell: (r) => (
        <Box>
          <Typography variant="body2">{r.allocationCode}</Typography>
          {r.allocationName ? <Typography variant="caption" color="text.secondary">{r.allocationName}</Typography> : null}
        </Box>
      ),
    },
    { id: 'lines', header: 'Receipts', cell: (r) => r.lineCount, align: 'right', hideOnMobile: true },
    {
      id: 'total',
      header: `Total ${currency}`.trim(),
      align: 'right',
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{formatAmount(r.total)}</Typography>
          {r.totalVat ? <Typography variant="caption" color="text.secondary">VAT {formatAmount(r.totalVat)}</Typography> : null}
        </Box>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => {
        const shown = r.displayStatus || r.status;
        const reimbursed = shown === 'Reimbursed' || !!r.reimbursedAt;
        const submitted = !['Draft', 'Cancelled', 'Rejected'].includes(r.status);
        return (
          <Stack spacing={0.5} alignItems="flex-start">
            <ClaimStatusChip status={shown} />
            {r.currentStageName && (r.status === 'Pending' || r.status === 'FinanceReview') ? <Typography variant="caption" color="text.secondary">{r.currentStageName}</Typography> : null}
            {!reimbursed && WAITING_STATUSES.includes(r.status) ? <WaitingChip days={r.waitingWorkingDays} overdue={!!r.overdue} /> : null}
            {reimbursed && r.reimbursedAt ? (
              <Typography variant="caption" color="text.secondary">Reimbursed {formatDate(r.reimbursedAt)}{r.reimburseReference ? ` · ${r.reimburseReference}` : ''}</Typography>
            ) : null}
            {submitted && r.originalsReceived === false && (r.originalsRequired || scope === 'originals') ? (
              <Typography variant="caption" sx={{ color: '#7a4b00' }}>Original receipts not received</Typography>
            ) : null}
            {r.waitingForMe ? <Typography variant="caption" sx={{ color: '#6b4b00', fontWeight: 600 }}>Waiting for you</Typography> : null}
          </Stack>
        );
      },
    },
    { id: 'submitted', header: 'Submitted', cell: (r) => formatDate(r.submittedAt), hideOnMobile: true },
    { id: 'updated', header: 'Updated', cell: (r) => formatDate(r.updatedAt), hideOnMobile: true },
  ], [currency, scope]);

  const caps = config?.capabilities;
  const bulkMode: 'approve' | 'reimburse' | null = scope === 'waiting' ? 'approve' : scope === 'reimburse' ? 'reimburse' : null;
  const selectable = useCallback((r: ClaimRow) => (bulkMode === 'approve' ? !!r.canBulkApprove : bulkMode === 'reimburse' ? !!r.canReimburse : false), [bulkMode]);
  const eligibleCount = rows.filter(selectable).length;
  const chosen = rows.filter((r) => selectable(r) && selected.has(r.claimId));
  const chosenTotal = round2(chosen.reduce((sum, r) => sum + (bulkMode === 'reimburse' ? reimburseAmount(r) : Number(r.total) || 0), 0));
  const claimsWord = (n: number) => `${n} claim${n === 1 ? '' : 's'}`;
  const closeBulk = (changed: boolean) => {
    setBulkOpen(false);
    if (changed) setReloadKey((k) => k + 1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1400 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Petty cash claims</Typography>
        {config ? <Typography variant="body2" color="text.secondary">{config.company.name}</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        {config && scope ? (
          <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />} onClick={() => { void exportList(); }} disabled={exporting || loading}>
            Export to Excel
          </Button>
        ) : null}
        {caps?.setup ? <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => navigate('/petty-cash/setup')}>Setup</Button> : null}
        {caps?.canCreate ? <Button startIcon={<AddIcon />} onClick={() => navigate('/petty-cash/new')} disabled={!config?.company.pettyCashEnabled}>New claim</Button> : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {listError && !error ? <Alert severity="error">{listError}</Alert> : null}
      {config && !config.company.pettyCashEnabled ? (
        <Alert severity="warning">Petty cash is not switched on for {config.company.name}. An administrator switches it on in Company Management.</Alert>
      ) : null}
      {config && config.company.pettyCashEnabled && caps && !caps.canCreate && caps.isHolder === false && scope === 'mine' ? (
        <Alert severity="info">You are not a petty cash holder in this company. Holders are set up in Petty Cash Setup.</Alert>
      ) : null}
      {config ? <HolderBanners config={config} companyId={companyId} onChanged={() => { void refresh(); }} /> : null}

      {config ? (
        <Box>
          <Tabs value={scope || false} onChange={(_e, v) => setScope(v)} aria-label="Which claims" sx={{ borderBottom: '1px solid #e3e8eb', mb: 1 }} variant="scrollable" allowScrollButtonsMobile>
            <Tab value="waiting" label="Waiting for me" />
            <Tab value="mine" label="My claims" />
            {caps?.viewAll ? <Tab value="all" label="All claims" /> : null}
            {caps?.canPost ? <Tab value="reimburse" label="To reimburse" /> : null}
            {caps?.finance || caps?.canPost ? <Tab value="originals" label="Originals pending" /> : null}
          </Tabs>
          {!loading && bulkMode && eligibleCount > 0 ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" role="status">
                {chosen.length
                  ? `${claimsWord(chosen.length)} ticked · ${formatAmount(chosenTotal)} ${currency}`.trim()
                  : bulkMode === 'approve' ? 'Tick the claims to approve together.' : 'Tick the claims to reimburse together.'}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {chosen.length ? <Button variant="text" onClick={() => setSelected(new Set())}>Clear</Button> : null}
              <Button onClick={() => setBulkOpen(true)} disabled={!chosen.length}>
                {bulkMode === 'approve' ? `Approve ${claimsWord(chosen.length)}…` : `Reimburse ${claimsWord(chosen.length)}…`}
              </Button>
            </Stack>
          ) : null}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
          ) : (
            <ResponsiveTable
              ariaLabel="Petty cash claims"
              columns={columns}
              rows={rows}
              getRowKey={(r) => r.claimId}
              onRowClick={(r) => navigate(`/petty-cash/${r.claimId}`)}
              emptyMessage={scope ? EMPTY[scope] : 'No claims yet'}
              selection={bulkMode && eligibleCount > 0 ? {
                selected,
                onChange: setSelected,
                isSelectable: selectable,
                label: (r) => `Select ${r.claimNo || `claim ${r.claimId}`}`,
              } : undefined}
            />
          )}
        </Box>
      ) : !error ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
      ) : null}

      {bulkMode === 'approve' ? (
        <BulkApproveDialog open={bulkOpen} rows={chosen} currency={currency} onClose={closeBulk} />
      ) : null}
      {bulkMode === 'reimburse' && config ? (
        <BulkReimburseDialog
          open={bulkOpen}
          rows={chosen}
          currency={currency}
          postInSap={processSettings(config.settings).reimbursePost}
          accounts={processSettings(config.settings)}
          onClose={closeBulk}
        />
      ) : null}
    </Box>
  );
};

export default PettyCashClaims;
